import { useEffect, useRef, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';

import Loader from '@/components/Loader';
import Button from '@/design_system/Button';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import {
  BrandRequestMainContent,
  BrandRequestMainFooter,
} from '@/layouts/Brand/BrandRequestLayout/BrandRequestLayout';
import {
  CheckoutData,
  isInPaymentStep,
  RequestWithRelations,
  usePayment,
  useRequest,
  useStartPayment,
} from '@/models/request';
import { ClientArticlesList } from '@/routes/Brand/Requests/Request/components/shared/ClientArticlesList';
import ClientStepper from '@/routes/Brand/Requests/Request/components/shared/ClientStepper';
import { PickupPointInfo } from '@/routes/Brand/Requests/Request/components/shared/PickupPointInfo/PickupPointInfo';
import { useStripe } from '@/services/stripe';

import { StepMessage } from '../../../shared/StepMessage';

import './Payment.css';

const Payment = ({ request }: { request: RequestWithRelations }) => {
  const { data: payment, isLoading: isLoadingPayment } = usePayment({ requestId: request.id });
  const [checkoutData, setCheckoutData] = useState<CheckoutData>();
  const returnParam = new URLSearchParams(window.location.search).get('return');
  // A cancelled or failed Nepting payment returns here so the customer can retry from the welcome step
  const isReturnFromCheckout = returnParam !== null && returnParam !== 'nepting-cancelled';

  if (isLoadingPayment) {
    return (
      <BrandRequestMainContent>
        <Loading>
          <Trans id="_general.loading">Loading...</Trans>
        </Loading>
      </BrandRequestMainContent>
    );
  }

  if (
    isReturnFromCheckout ||
    payment?.status !== 'unstarted' ||
    checkoutData?.connector === 'mock'
  ) {
    return (
      <BrandRequestMainContent>
        <PaymentStatus request={request} />
      </BrandRequestMainContent>
    );
  }

  if (checkoutData?.connector === 'stripe' && checkoutData?.stripe.clientSecret) {
    return (
      <BrandRequestMainContent>
        <StripePaymentCheckout clientSecret={checkoutData.stripe.clientSecret} />
      </BrandRequestMainContent>
    );
  }

  if (checkoutData?.connector === 'nepting') {
    return (
      <BrandRequestMainContent>
        <NeptingPaymentRedirect
          paymentUrl={checkoutData.nepting.paymentUrl}
          fields={checkoutData.nepting.fields}
        />
      </BrandRequestMainContent>
    );
  }

  return <PaymentWelcome request={request} setCheckoutData={setCheckoutData} />;
};

const PaymentWelcome = ({
  request,
  setCheckoutData,
}: {
  request: RequestWithRelations;
  setCheckoutData: (data: CheckoutData) => void;
}) => {
  const { t } = useLingui();

  const { mutateAsync: startPayment, isPending: isPendingPayment } = useStartPayment();
  const [apiError, setApiError] = useState<string | null>(null);

  const isOnlinePayment = request.articles.some(
    (article) => article.step?.step === 'payment' && article.step?.config.method === 'online'
  );

  const onSubmit = async () => {
    setApiError(null);

    try {
      const checkoutData = await startPayment({ requestId: request.id });

      setCheckoutData(checkoutData);
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <>
      <BrandRequestMainContent>
        <Stack gap="1.5rem">
          <ClientStepper request={request} />

          <StepMessage
            message={
              <Trans id="client.request.payment.welcome.title.paying">
                A payment is required from your side.
              </Trans>
            }
          />

          <Stack gap="1rem">
            <PickupPointInfo request={request} />
            <ClientArticlesList request={request} showPrice showArticleComment />
          </Stack>

          {apiError && (
            <Message type="error" style={{ marginTop: '2rem' }}>
              <b>{apiError}</b>
            </Message>
          )}
        </Stack>
      </BrandRequestMainContent>

      {isOnlinePayment && (
        <BrandRequestMainFooter>
          <Button
            variant="brand"
            size="large"
            onPress={onSubmit}
            isLoading={isPendingPayment}
            style={{ width: '100%' }}
          >
            <Trans id="client.request.payment.proceed">Proceed to payment</Trans>
          </Button>
        </BrandRequestMainFooter>
      )}
    </>
  );
};

const StripePaymentCheckout = ({ clientSecret }: { clientSecret: string }) => {
  const stripe = useStripe();

  if (!stripe) {
    return (
      <Loading>
        <Trans id="_general.loading">Loading...</Trans>
      </Loading>
    );
  }

  return (
    <Stack padding="16px" style={{ flex: 1 }}>
      {clientSecret && (
        <EmbeddedCheckoutProvider stripe={stripe} options={{ clientSecret }}>
          <EmbeddedCheckout className="stripe-checkout" />
        </EmbeddedCheckoutProvider>
      )}
    </Stack>
  );
};

const NeptingPaymentRedirect = ({
  paymentUrl,
  fields,
}: {
  paymentUrl: string;
  fields: Record<string, string>;
}) => {
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    formRef.current?.submit();
  }, []);

  return (
    <>
      <Loading>
        <Trans id="client.request.payment.redirect">
          Redirecting you to the payment page, please wait...
        </Trans>
      </Loading>
      <form ref={formRef} method="POST" action={paymentUrl} style={{ display: 'none' }}>
        {Object.entries(fields).map(([name, value]) => (
          <input key={name} type="hidden" name={name} value={value} />
        ))}
      </form>
    </>
  );
};

const PaymentStatus = ({ request }: { request: RequestWithRelations }) => {
  /**
   * We refetch the request every second to check if the request is still in the Payment step.
   * Once the payment succeeds, the articles will be moved to their next step.
   * Therefore, they won't be in the Payment step anymore, and the `Payment` component will be unmounted.
   */
  useRequest(request.id, {
    refetchInterval: ({ state: { data } }) => (data && isInPaymentStep(data) ? 1000 : false),
  });

  return (
    <Loading>
      <Trans id="client.request.payment.in-progress">
        Your payment is being processed, please wait...
      </Trans>
    </Loading>
  );
};

const Loading = ({ children }: { children: React.ReactNode }) => {
  return (
    <Stack
      style={{ height: '100%' }}
      gap="1rem"
      alignItems="center"
      flexWrap="nowrap"
      justifyContent="center"
    >
      <Loader style={{ height: '40px', width: '40px' }} />
      <Paragraph>{children}</Paragraph>
    </Stack>
  );
};

export default Payment;
